import React, { useState, useEffect, useCallback } from "react";
import { Box, Grid } from "@mui/material";
import { Route, Routes } from "react-router-dom";
import {
  getCurrentUser,
  signOut,
  fetchUserAttributes,
  fetchAuthSession,
} from "aws-amplify/auth";
import { send_request } from "../../utils/Request";
import useProjectStore from "../ProjectCanvas/useProjectStore";
import AppRoutes from "../../Routes";
import ContextAgentProvider from "../contextAgent/ContextAgentProvider";
import Sidebar from "./Sidebar";
import W86Backdrop from "./W86Backdrop";
import { CustomSignIn } from "./CustomSignIn";
import aws_config from "../../auth/awsConfig";
import CreditsExhaustedDialog from "../common/CreditsExhaustedDialog";
import FreeTierOnboardingGate from "../billing/FreeTierOnboardingGate";
import ByokInvalidBanner from "../billing/ByokInvalidBanner";
import { fetchCreditBalance, fetchSubscription } from "../../utils/billingApi";
import { useLastActiveHeartbeat } from "../../hooks/useLastActiveHeartbeat";

function isAuthRevokedError(error) {
  const message = error?.message || "";
  const code = error?.code || error?.name || "";
  return (
    code === "NotAuthorizedException" ||
    message === "No current user" ||
    message.includes("Access Token has been revoked")
  );
}

function AppShell({
  authState,
  updateUsername,
  onSignOut: parentSignOut,
  onStateChange,
}) {
  const [state, setState] = useState({
    roleAdministrator: false,
    roleEditor: false,
    rolePrivateViewer: false,
    tier: "",
    orgName: "",
    clientId: null,
    userDisplayName: "",
    firstLogin: false,
    initialized: false,
  });

  const handleSignOut = useCallback(async () => {
    try {
      await signOut();
      const gapi = window.gapi;
      if (gapi) {
        await new Promise((resolve) => gapi.load("auth2", resolve));
        const auth2 = await gapi.auth2.init({
          client_id: aws_config.gg_client_id,
        });
        if (auth2.isSignedIn.get()) {
          await auth2.signOut();
        }
      }
      window.location.pathname = "/";
    } catch (err) {
      console.error("Error signing out:", err);
      window.location.pathname = "/";
    }
  }, []);

  const [creditsExhaustedOpen, setCreditsExhaustedOpen] = useState(false);
  const [creditsExhaustedMessage, setCreditsExhaustedMessage] = useState("");
  const [creditsExhaustedReason, setCreditsExhaustedReason] = useState("");

  useEffect(() => {
    const handler = (event) => {
      setCreditsExhaustedMessage(event.detail?.message || "");
      setCreditsExhaustedReason(event.detail?.reason || "");
      setCreditsExhaustedOpen(true);
    };
    window.addEventListener("workflow86:credits-exhausted", handler);
    return () =>
      window.removeEventListener("workflow86:credits-exhausted", handler);
  }, []);

  useEffect(() => {
    const handler = (event) => {
      const state = event.detail?.state;
      if (state) {
        useProjectStore.setState({ billingFreeTierState: state });
      }
    };
    window.addEventListener("workflow86:free-tier-locked", handler);
    return () => window.removeEventListener("workflow86:free-tier-locked", handler);
  }, []);

  const initializeBillingSystem = useCallback(async () => {
    try {
      const subscription = await fetchSubscription();
      const balance = await fetchCreditBalance();
      const planId = subscription?.plan || "FREE";
      useProjectStore.setState({
        billingPlan: planId,
        creditBalance: balance?.includedBalance,
        billingFreeTierState: subscription?.freeTier?.state || null,
        billingInitialized: true,
      });
      setState((prev) => ({ ...prev, tier: planId }));
      useProjectStore.setState({ tier: planId });
    } catch (error) {
      console.error("Error initializing billing system:", error);
      useProjectStore.setState({ billingInitialized: true });
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function initialize() {
      useProjectStore.setState({
        billingInitialized: false,
        billingPlan: undefined,
        creditBalance: undefined,
        billingFreeTierState: undefined,
      });

      let email = null;
      try {
        const { accessToken, idToken } =
          (await fetchAuthSession()).tokens ?? {};
        const currentUser = await getCurrentUser();
        const userAttributes = await fetchUserAttributes();
        email = userAttributes.email || idToken.payload.email;
      } catch (error) {
        console.log("Error fetching auth session:", error);
        if (isAuthRevokedError(error)) {
          await handleSignOut();
          return;
        }
      }

      const [checkUserResponse, json] = await Promise.all([
        email
          ? send_request(
              "authz-service/check-user-verified-and-completed",
              { email },
              "",
              "POST"
            ).catch((error) => {
              console.log(
                "An error occurred while checking user status:",
                error
              );
              return null;
            })
          : Promise.resolve(null),
        send_request("authz-service/user-info", "", {}),
      ]);

      if (cancelled) return;

      let firstLogin = false;
      if (checkUserResponse?.data) {
        firstLogin = checkUserResponse.data.verified === false;
      }

      if (json?.data) {
        const d = json.data;
        setState({
          roleAdministrator: d.roleAdministrator,
          roleEditor: d.roleEditor,
          rolePrivateViewer: d.rolePrivateViewer,
          tier: d.client.tier,
          orgName: d.client.clientName || "",
          clientId: d.client.clientId || null,
          userDisplayName: d.displayName || "",
          firstLogin,
          initialized: true,
        });

        useProjectStore.setState({
          timezone: d.timezone,
          tier: d.client.tier,
          orgName: d.client.clientName,
          clientId: d.client.clientId,
          userName: d.userName,
          userDisplayName: d.displayName || d.userName,
        });

        await initializeBillingSystem();

        fetchUserAttributes().catch(async (error) => {
          console.log("Error fetching user attributes:", error);
          if (isAuthRevokedError(error)) {
            await handleSignOut();
          }
        });

        if (useProjectStore.getState().timezone == null) {
          useProjectStore.setState({
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          });
        }
      } else {
        useProjectStore.setState({
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        });
        setState((prev) => ({ ...prev, firstLogin, initialized: true }));
      }
    }

    initialize();
    return () => {
      cancelled = true;
    };
  }, [handleSignOut, initializeBillingSystem]);

  useLastActiveHeartbeat(
    state.initialized && authState === "signedIn" && !state.firstLogin
  );

  if (!state.initialized) return null;

  if (state.firstLogin) {
    return (
      <Routes>
        <Route
          path="/"
          element={
            <CustomSignIn
              authState={authState}
              updateUsername={updateUsername}
              onStateChange={onStateChange}
              firstLogin={(val) =>
                setState((prev) => ({ ...prev, firstLogin: val }))
              }
            />
          }
        />
      </Routes>
    );
  }

  if (authState !== "signedIn") return null;

  return (
    <Grid
      container
      direction="column"
      style={{ height: "100vh", minWidth: 0, overflow: "hidden" }}
    >
      <W86Backdrop />
      <ByokInvalidBanner />
      <FreeTierOnboardingGate />
      <CreditsExhaustedDialog
        open={creditsExhaustedOpen}
        message={creditsExhaustedMessage}
        reason={creditsExhaustedReason}
        onClose={() => setCreditsExhaustedOpen(false)}
      />
      <Grid
        item
        style={{
          display: "flex",
          flex: 1,
          minWidth: 0,
          overflow: "hidden",
        }}
      >
        <ContextAgentProvider
          sidebar={
            <Sidebar
              roleAdministrator={state.roleAdministrator}
              roleEditor={state.roleEditor}
              rolePrivateViewer={state.rolePrivateViewer}
              clientId={state.clientId}
              tier={state.tier}
              userDisplayName={state.userDisplayName}
              orgName={state.orgName}
              onSignOut={handleSignOut}
            />
          }
        >
          <AppRoutes firstLogin={state.firstLogin} />
        </ContextAgentProvider>
      </Grid>
    </Grid>
  );
}

export default AppShell;
